import { useEffect, useRef, useLayoutEffect, useState } from "react";
import queryString from "query-string";
import GalleryComponentsWrapper from "./GalleryComponentsWrapper.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import sanitizeMailId from "helpers/sanitizeMailId.ts";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { useMail } from "../../../../contexts/mailContext/mailContext.js";
import { useMailStatus } from "./hooks/useMailStatus.js";
import { useMailView } from "../../../../contexts/mailViewContext/mailViewContext.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import { useParams, useLocation } from "react-router-dom";
import { useComments } from "../../../../contexts/commentsContext/commentsContext.js";
import { findFolderWithConditions } from "utils/gridHelpers.js";
import { GetUsersContributersToMailApiRequest } from "api/MailsController.ts";

import "./GalleryContent.scss";

const GalleryContent = () => {
  const {
    fetchMailFiles,
    fetchMailInfo,
    setMailParams,
    getMailId,
    getMailStatus,
    getCurrentFolder,
    fetchMailPaymentInfo,
    getIsDisplayAllowed,
    getSorting,
    cleanUpFunction,
    setUserVideoToken,
    setFilesList,
    resetFilesList,
    fetchCollectionsData,
    getCurrentCollection,
    getSender,
    getMailSecurity,
    getMailMetaData,
    setIsDisplayAllowed,
    resetPageNumber,
    setContributorsData,
    getFolderStructured,
    getSearchParam,
    getContributorsFilter,
    getFilesLoading,
    getFoldersData,
    getFoldersList,
    setCurrentFolder,
    getIsCollection,
  } = useMail();

  const { setFilesSelection } = useMailView();

  const {
    isBasicSettingsInitialized,
    getBasicSettingsData,
    fetchAdsBackground,
    getLanguage,
  } = useContent();
  const { fetchCommentsData } = useComments();

  const { getUserData, isLoggedIn } = useUser();

  const { redirectTo, fullScreenMessage, setFullScreenMessage } =
    useMailStatus();

  useLayoutEffect(() => {
    //basic settings initialized
    if (isBasicSettingsInitialized()) {
      setUserVideoToken(getBasicSettingsData().userVideoToken);
    }
  }, [isBasicSettingsInitialized()]);

  const { mailId, fileId, deleteMailParams, anotherParam } = useParams();
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const senderMail = searchParams.senderMail;
  const [verifiedSenderMailParams, setVerifiedSenderMailParams] =
    useState(false);
  const verifiedJumboFormDeleteLinkParam = async () => {
    const parameters = {
      encryptedMailId: mailId,
      hashCodeParam: deleteMailParams,
    };
    const response = await apiRequest(
      "Mails",
      "VerifiedJumboFormDeleteLinkParam",
      parameters
    );
    if (response.status === "success") {
      if (response.data === "ok") {
        setVerifiedSenderMailParams(true);
      }
    }
  };

  useEffect(() => {
    if (deleteMailParams && deleteMailParams !== undefined) {
      verifiedJumboFormDeleteLinkParam();
    }
  }, [deleteMailParams]);

  // AD-QA section by anotherparm in url
  useEffect(() => {
    if (anotherParam?.includes("preview-ad-")) {
      fetchAdsBackground();
    }
  }, [anotherParam]);

  // hook to preview items, cos fileId is not available sometimes on render
  const fileIdExist = useRef(false);

  // in case once i got fileId from params, i need to keep it in memory
  useEffect(() => {
    if (fileId) {
      fileIdExist.current = fileId;
    }
  }, [fileId]);

  // now i`m setting fileId even if it is not available on render
  useEffect(() => {
    if (mailId) {
      if (fileIdExist.current && !fileId) {
        //component did mount and ready to get data from API
        setMailParams({
          fileId: fileIdExist.current,
          mailId,
          isCollection: location.pathname.includes("collection"),
        });
      } else {
        //component did mount and ready to get data from API
        setMailParams({
          fileId,
          mailId,
          isCollection: location.pathname.includes("collection"),
        });
      }
    }
  }, [mailId, fileId]);

  useEffect(() => {
    return () => {
      cleanUpFunction();
    };
  }, []);

  useEffect(() => {
    setFilesSelection([]);
  }, [getCurrentFolder(), getSorting()]);

  // HANDLE CASE WHEN USER UPLOAD FILES WITHIN ONLY NESTED FOLDER

  const autoSelectFolderRef = useRef(false);
  const wasTriggeredOnce = useRef(false);

  useEffect(() => {
    if (wasTriggeredOnce.current) return;
    const foldersList = getFoldersList();

    if (foldersList.length > 0 && !getCurrentFolder()) {
      const rootFolderId = 1;

      const resultFolderId = findFolderWithConditions(
        rootFolderId,
        getFoldersData()
      );

      if (resultFolderId) {
        wasTriggeredOnce.current = true;
        if (getFilesLoading()) {
          autoSelectFolderRef.current = resultFolderId;
        } else {
          setCurrentFolder(resultFolderId);
        }
      }
    }
  }, [getFoldersList()]);

  // autoSelectFodlerRef - will be equal to some folderId with files or >2 nested folders,
  useEffect(() => {
    if (autoSelectFolderRef.current) {
      setCurrentFolder(autoSelectFolderRef.current);
      autoSelectFolderRef.current = false;
    }
  }, [getFilesLoading()]);

  // IN THIS CASE NEED TO AUTOSELECT FIRST FOLDER WITH FILES OR FIRST FOLDER WITH 2 FOLDERS
  const asyncFetchMailFiles = async () => {
    if (!getMailId() || getFolderStructured() === null) return;
    const res = await fetchMailFiles({ reset: true });
    resetPageNumber();
    if (res === "sender-is-suspended") {
      setFullScreenMessage({
        data: {
          component: "SuspendedSenderMessage",
        },
        isShow: true,
      });
      setIsDisplayAllowed(false);
    }
  };

  // on each dependency do fetch of files
  useEffect(() => {
    if (getIsDisplayAllowed()) {
      asyncFetchMailFiles();
    } else {
      resetPageNumber();
      resetFilesList();
    }
  }, [
    getCurrentFolder(),
    getSorting().by,
    getSorting().asc,
    getCurrentCollection(),
    getIsDisplayAllowed(),
    getFolderStructured(),
    getSearchParam(),
    getMailId(),
    getContributorsFilter(),
  ]);

  //LOGIC RELOAD PAGE IF INACTIVE
  useEffect(() => {
    const timeoutInMiliseconds = 3600000;
    let timeoutId;
    let needRestart = false;

    const resetTimer = () => {
      if (needRestart) {
        resetPageNumber();
      }
      needRestart = false;
      window.clearTimeout(timeoutId);
      startTimer();
    };
    const startTimer = () => {
      // window.setTimeout returns an Id that can be used to start and stop a timer
      timeoutId = window.setTimeout(doInactive, timeoutInMiliseconds);
    };

    const doInactive = () => {
      needRestart = true;
      // does whatever you need it to actually do - probably signs them out or stops polling the server for info
    };

    function setupTimers() {
      document.addEventListener("mousemove", resetTimer, false);
      document.addEventListener("mousedown", resetTimer, false);
      document.addEventListener("keypress", resetTimer, false);
      document.addEventListener("scroll", resetTimer, false);
      document.addEventListener("touchmove", resetTimer, false);
      startTimer();
    }
    setupTimers();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (getMailId()) {
      //fetched mail-id from  params
      fetchMailInfo();
      fetchCommentsData(getMailId());
      if (!getIsCollection()) {
        fetchCollectionsData();
      }
    }
  }, [getMailId(), isLoggedIn()]);

  // getting info about contributors
  const handleGetContributors = async () => {
    const mailId = sanitizeMailId(getMailId());

    if (!mailId) return;

    const res = await GetUsersContributersToMailApiRequest(mailId);

    if (res.status === "success") {
      setContributorsData(res.data);
    } else if (res.data === "Internal error") {
      // redirect to error page
      window.location.href = `/l/${getLanguage()}/error`;
    }
  };

  useEffect(() => {
    if (getMailId()) {
      handleGetContributors();
    }
  }, [getMailId()]);

  //fetch jumbompay info after logging out/in
  useEffect(() => {
    getMailId() && fetchMailPaymentInfo();
  }, [getUserData(), getMailId()]);

  // logic of recipients only security options
  useEffect(() => {
    const isMailSender = getUserData().UserName === getSender().email;
    let isMailSenderByParams = senderMail
      ? getMailMetaData()?.JumboFormEncryptedSenderMail === senderMail
      : false;

    if (deleteMailParams && verifiedSenderMailParams) {
      isMailSenderByParams = true;
    }

    const conditionForRecipientsOnly =
      getMailSecurity()?.recipientsOnly &&
      (isMailSender ||
        (getUserData().UserName &&
          !getMailMetaData()?.RecipientsList?.includes(
            getUserData().UserName
          )));
    const conditionEmbedOwner =
      !isMailSenderByParams &&
      getMailMetaData()?.EmbedDownloadOnlyOwner &&
      (getUserData()?.UserName === getMailMetaData()?.EmailOfEmbedOwner ||
        getUserData()?.TopUserEmail === getMailMetaData()?.EmailOfEmbedOwner);
    if (
      fullScreenMessage &&
      (conditionForRecipientsOnly || conditionEmbedOwner)
    ) {
      setFullScreenMessage({ ...fullScreenMessage, isShow: false });
    }
  }, [getUserData()]);

  //reset files list if display is not allowed
  useEffect(() => {
    !getIsDisplayAllowed() ? resetFilesList() : setFilesList();
  }, [getIsDisplayAllowed()]);

  return getMailStatus() ? (
    <GalleryComponentsWrapper
      {...{ redirectTo, fullScreenMessage, setFullScreenMessage }}
    />
  ) : (
    <div className="height-100"></div>
  );
};

export default GalleryContent;
